<template>
  <div>
    <!-- Breadcrumb -->
    <nav v-if="breadcrumb" class="border-b border-gray-300 py-2 px-2 mb-4 md:px-0">
      <div class="flex justify-between items-center">
        <div>
          <div class="hidden md:flex text-sm text-gray-500">
            <router-link :to="{ name: 'home' }" class="hover:text-primary">Home</router-link>
            <span class="mx-2">/</span>
            <router-link :to="{ name: 'organization', params: { orgId: orgId } }" class="hover:text-primary">Organization</router-link>
            <span class="mx-2">/</span>
            <span class="text-gray-700">Survey Reports</span>
          </div>
          <div class="text-2xl text-primary font-bold mt-2">Survey Reports</div>
        </div>
      </div>
    </nav>

    <!-- Filters -->
    <div class="pb-4">
      <div class="bg-white border rounded-lg shadow p-4">
        <div class="font-semibold text-gray-700 text-lg mb-4">Filters</div>
          <button 
    @click="exportToCSV"
    :disabled="!responses.length"
    class="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 disabled:opacity-50">
    Export CSV
  </button>

        <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
          <!-- Survey Selector -->
          <div class="flex-1">
            <label class="block text-gray-600 font-medium mb-1">Select Survey</label>
            <select v-model="surveyId"
              class="block w-full bg-white border border-gray-300 px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:border-primary">
              <option value="" disabled>Select a Survey</option>
              <option v-for="s in surveys" :key="s.id" :value="s.id">
                {{ s.title }}
              </option>
            </select>
          </div>

          <!-- Date Range -->
          <div>
            <label class="block text-gray-600 font-medium mb-1">From</label>
            <input type="date" v-model="startDate"
              class="block w-full border border-gray-300 rounded-lg py-2 px-3 shadow-sm focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label class="block text-gray-600 font-medium mb-1">To</label>
            <input type="date" v-model="endDate"
              class="block w-full border border-gray-300 rounded-lg py-2 px-3 shadow-sm focus:outline-none focus:border-primary" />
          </div>

          <!-- Load Button -->
          <div>
            <button @click="fetchSurveyResponses(1)"
              class="px-4 py-2 bg-primary text-white rounded-lg shadow hover:bg-primary-dark">
              Load Report
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto rounded-lg shadow">
          <table v-if="!loading && responses.length" class="table-auto w-full border-collapse">
            <thead>
              <tr class="bg-gray-100 text-gray-700 text-sm uppercase tracking-wide">
                <th class="px-4 py-3 text-left">#</th>
                <th class="px-4 py-3 text-left">Question</th>
                <th class="px-4 py-3 text-left">Response</th>
                <th class="px-4 py-3 text-left">Firstname</th>
                <th class="px-4 py-3 text-left">Lastname</th>
                <th class="px-4 py-3 text-left">Email</th>
                <th class="px-4 py-3 text-left">Phone</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in responses" :key="r.id"
                class="border-t hover:bg-gray-50 transition-colors">
                <td class="px-4 py-3 font-bold">{{ idx + 1 + (pagination.current_page - 1) * perPage }}</td>
                <td class="px-4 py-3">{{ r.surveyquestion }}</td>
                <td class="px-4 py-3">{{ r.surveyresponse }}</td>
                <td class="px-4 py-3">{{ r.firstname || '—' }}</td>
                <td class="px-4 py-3">{{ r.lastname || '—' }}</td>
                <td class="px-4 py-3">{{ r.email || '—' }}</td>
                <td class="px-4 py-3">{{ r.phonenumber || '—' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Loading -->
          <div v-if="loading" class="py-6 text-center w-full text-gray-600">
            <feather animation="spin" type="loader" size="32" class="mr-2"></feather>
            <p>Loading...</p>
          </div>

          <!-- No Data -->
          <div v-if="!loading && responses.length === 0" class="py-6 text-center text-gray-500">
            No responses found.
          </div>
        </div>

        <!-- Pagination -->
<div v-if="responses.length" class="py-4 flex justify-between items-center">
  <button 
    class="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
    :disabled="pagination.current_page === 1"
    @click="fetchSurveyResponses(pagination.current_page - 1)">
    Prev
  </button>

  <!-- Page Numbers with Ellipsis -->
  <div class="flex gap-2">
    <button
      v-for="page in visiblePages"
      :key="page"
      @click="typeof page === 'number' && fetchSurveyResponses(page)"
      class="px-3 py-1 rounded"
      :class="page === pagination.current_page 
        ? 'bg-primary text-white shadow' 
        : page === '...' 
          ? 'cursor-default bg-transparent text-gray-500' 
          : 'bg-gray-100 hover:bg-gray-200'"
      :disabled="page === '...'"
    >
      {{ page }}
    </button>
  </div>

  <button 
    class="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
    :disabled="pagination.current_page === pagination.last_page"
    @click="fetchSurveyResponses(pagination.current_page + 1)">
    Next
  </button>
</div>

      </div>
    </div>

    <!-- Analytics -->
    <div v-if="responses.length" class="bg-white border rounded-lg shadow p-4 mt-6">
      <div class="font-semibold text-gray-700 text-lg mb-4">Analytics Summary</div>
      <bar-chart :chart-data="chartData" :options="chartOptions" class="w-full h-96" />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { Bar } from "vue-chartjs";
import * as XLSX from "xlsx";
export default {
  name: "SurveyReports",
  components: {
    BarChart: {
      extends: Bar,
      props: ["chartData", "options"],
      mounted() {
        this.renderChart(this.chartData, this.options);
      },
      watch: {
        chartData: {
          deep: true,
          handler(newVal) {
            this.renderChart(newVal, this.options);
          }
        }
      }
    }
  },
  data() {
    return {
      surveys: [],
      surveyId: "",
      startDate: "",
      endDate: "",
      responses: [],
      pagination: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
      perPage: 20,
      loading: false,
      error: null,
      breadcrumb: true,
      orgId: this.$route.params.orgId || null,
      chartData: { labels: [], datasets: [{ label: "Responses Count", backgroundColor: "#3B82F6", data: [] }] },
      chartOptions: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top" }, title: { display: true, text: "Survey Responses Summary" } } }
    };
  },
  async created() {
  //  await this.fetchSurveys();
  },
  methods: {
//  async exportToCSV() {
//   try {
//     if (!this.surveyId) {
//       alert("Please select a survey first.");
//       return;
//     }

//     this.loading = true;
//     this.error = null;

//     console.log("Fetching all pages for export...");

//     // 1️⃣ Fetch first page to know total pages
//     const first = await axios.post("https://apiresources.brandfi.co.ke/api/v1/survey/responses", {
//       survey_id: this.surveyId,
//       page: 1,
//       per_page: this.perPage,
//       start_date: this.startDate || null,
//       end_date: this.endDate || null
//     });

//     const pagination = first.data && first.data.pagination ? first.data.pagination : { last_page: 1 };
//     const totalPages = pagination.last_page || 1;
//     let allResponses = first.data.responses || [];

//     // 2️⃣ Fetch remaining pages sequentially
//     for (let page = 2; page <= totalPages; page++) {
//       console.log(`Fetching page ${page}/${totalPages}`);
//       const res = await axios.post("https://apiresources.brandfi.co.ke/api/v1/survey/responses", {
//         survey_id: this.surveyId,
//         page: page,
//         per_page: this.perPage,
//         start_date: this.startDate || null,
//         end_date: this.endDate || null
//       });

//       if (res.data && res.data.responses && res.data.responses.length > 0) {
//         allResponses = allResponses.concat(res.data.responses);
//       }
//     }

//     if (!allResponses.length) {
//       alert("No survey responses found to export.");
//       return;
//     }

//     // 3️⃣ Build CSV
//     const headers = [
//       "#",
//       "Question",
//       "Response",
//       "Firstname",
//       "Lastname",
//       "Email",
//       "Phone"
//     ];

//     const rows = allResponses.map((r, idx) => [
//       idx + 1,
//       r.surveyquestion || "",
//       r.surveyresponse || "",
//       r.firstname || "",
//       r.lastname || "",
//       r.email || "",
//       r.phonenumber || ""
//     ]);

//     let csv = headers.join(",") + "\n";
//     rows.forEach(row => {
//       csv += row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(",") + "\n";
//     });

//     // 4️⃣ Download
//     const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
//     const url = URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.setAttribute("href", url);
//     link.setAttribute("download", `survey_report_${this.surveyId}.csv`);
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//     URL.revokeObjectURL(url);

//     console.log("✅ CSV exported successfully:", allResponses.length, "records");
//   } catch (e) {
//     console.error("Export failed:", e);
//     this.error = "Failed to export survey responses.";
//   } finally {
//     this.loading = false;
//   }
// },
async exportToCSV(page = 1) {
  try {
    this.loading = true;

    // Get current page data directly from API
    const response = await axios.post("https://apiresources.brandfi.co.ke/api/v1/survey/responses", {
          survey_id: this.surveyId,
          page,
          per_page: this.perPage,
          start_date: this.startDate || null,
          end_date: this.endDate || null
        });

    const responses = response.data.responses || [];

    if (!responses.length) {
      this.$root.$emit("alert_error", [
            "No data found on this page to export."
          ]);
      this.loading = false;
      return;
    }

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(responses);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Survey Responses');

    // Save file
    XLSX.writeFile(wb, `survey_report_page_${this.currentPage}.xlsx`);
  } catch (error) {
   // console.error('Error exporting:', error);
    this.$root.$emit("alert_error", [
            "Failed to export this page."
          ]);
  //  this.$toast.error('Failed to export this page.');
  } finally {
    this.loading = false;
  }
},

    async fetchSurveys() {
      try {
        const res = await axios.get("https://apiresources.brandfi.co.ke/api/v1/survey");
        this.surveys = res.data || [];
      } catch (e) {
        console.error(e);
        this.error = "Failed to load surveys list.";
      }
    },
    async fetchSurveyResponses(page = 1) {
      if (!this.surveyId) {
        this.error = "Please select a survey";
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.post("https://apiresources.brandfi.co.ke/api/v1/survey/responses", {
          survey_id: this.surveyId,
          page,
          per_page: this.perPage,
          start_date: this.startDate || null,
          end_date: this.endDate || null
        });
        this.responses = res.data.responses || [];
        this.pagination = res.data.pagination || this.pagination;
        this.updateChartData();
      } catch (e) {
        console.error(e);
        this.error = "Failed to fetch survey responses";
      } finally {
        this.loading = false;
      }
    },
    updateChartData() {
      const grouped = {};
      this.responses.forEach(r => {
        grouped[r.surveyresponse] = (grouped[r.surveyresponse] || 0) + 1;
      });
      this.chartData = {
        labels: Object.keys(grouped),
        datasets: [{ label: "Responses Count (Current Page)", backgroundColor: "#3B82F6", data: Object.values(grouped) }]
      };
    }
  },
  computed: {
  visiblePages() {
    const pages = [];
    const total = this.pagination.last_page;
    const current = this.pagination.current_page;

    if (total <= 7) {
      // Show all if not too many
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1); // Always show first page

      if (current > 3) pages.push("...");

      const start = Math.max(2, current - 2);
      const end = Math.min(total - 1, current + 2);

      for (let i = start; i <= end; i++) pages.push(i);

      if (current < total - 2) pages.push("...");

      pages.push(total); // Always show last page
    }

    return pages;
  }
}

};
</script>
